import React, { forwardRef, ForwardRefRenderFunction, useMemo } from "react";
import { AppState } from "store";
import { useSelector } from "react-redux";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { FormikProps } from "formik";
import { FormFieldConstants, SalaryDeviationConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { SalaryDeviationLeaveFormContainerProps } from "models/propTypes/salaryDeviationPropTypes";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import CreateLeaveForm from "./leaveForm";
import { useFloatingMessages, useMountedState, LoadingWithLogo, StaticInfoMessage } from "components/common";
import { useSalaryDeviationsService } from "serviceHooks";
import useDeviationPermissions from "../usePermissions";
import useIsReadOnly from "./useIsReadOnly";
//#endregion
import { DateHelper, LocalizationHelperInstance, UtilsHelper, WorkerHelper } from "utils/helpers";
import * as Yup from "yup";
import cssExports from "theme/styles/main.module.scss";

const CreateLeaveFormContainer: ForwardRefRenderFunction<FormHandles, SalaryDeviationLeaveFormContainerProps> = (
  props,
  ref
) => {
  const salaryDeviationsService = useSalaryDeviationsService();
  const { salaryDeviationId, monthState, yearMonth, personId, personName } = props;
  const { getMounted } = useMountedState();
  const [loading, setLoading] = React.useState(false);

  const { isSalaryAdmin, isBusinessUnitManagerWithSalaryRights, isDeviationAdmin } = useDeviationPermissions();
  const [deviationType, setDeviationType] = React.useState<WintSuperkollAPIModel.SalaryDeviationType>();
  const currentEmployee = useSelector((state: AppState) => state.auth.loggedInUser);
  const allowedDeviationTypes = useSelector((state: AppState) => state.auth.selectedCompany.allowedDeviationTypes);

  const enabledLeaveTypes = SalaryDeviationConstants.LeaveTypes.concat(
    SalaryDeviationConstants.TimeReportingDeviationTypes
  ).filter((value) => allowedDeviationTypes?.includes(value) || deviationType === value);

  const [initialValues, setInitialValues] = React.useState<SalaryDeviationFormTypes.CreateLeave>({
    deviationType: enabledLeaveTypes[0],
    personId: personId ?? currentEmployee.personId,
    personName: personName ?? currentEmployee.name,
    yearAndMonth: yearMonth ?? DateHelper.getYearMonth(new Date()),
    dayHours: [],
    fillDayHours: false,
    hasConnectedTimeReports: false,
  });

  const { enqueueMessage } = useFloatingMessages();

  const loadSalaryDeviation = (id: number) => {
    props.onFormValidationChange && props.onFormValidationChange(false);
    setLoading(true);
    salaryDeviationsService
      .get(id)
      .then((salaryDeviation) => {
        if (getMounted()) {
          setDeviationType(salaryDeviation.deviationType);

          const restrictToMonth = salaryDeviation.yearAndMonth
            ? DateHelper.getDateFromYearMonth(salaryDeviation.yearAndMonth)
            : undefined;
          const validStartDay = restrictToMonth ? DateHelper.getFirstDayOfMonth(restrictToMonth) : undefined;
          const validEndDay = restrictToMonth ? DateHelper.getFirstDayOfNextMonth(restrictToMonth) : undefined;

          setInitialValues({
            ...salaryDeviation,
            fromDate: salaryDeviation.fromDate ? new Date(salaryDeviation.fromDate) : undefined,
            toDate: salaryDeviation.toDate ? new Date(salaryDeviation.toDate) : undefined,
            dayHours: salaryDeviation.dayHours?.map(
              (item): SalaryDeviationFormTypes.HoursByDay => ({
                id: UtilsHelper.getUniqueId(),
                day: new Date(item.day),
                hours: Math.trunc(item.hours ?? 0),
                minutes: (item.hours - Math.trunc(item.hours ?? 0)) * 60,
              })
            ),
            fillDayHours: salaryDeviation.dayHours && salaryDeviation.dayHours.length > 0,
            restrictDateRange: { start: validStartDay, end: validEndDay },
            customName: salaryDeviation.customDeviationName,
            isAdminOnlyType: salaryDeviation.isAdminOnlyType,
          });
        }
      })
      .then(() => {
        setLoading(false);
        props.onFormValidationChange && props.onFormValidationChange(true);
      });
  };

  React.useEffect(() => {
    if (salaryDeviationId) {
      loadSalaryDeviation(salaryDeviationId);
    }
  }, [salaryDeviationId]);

  const validationSchema = useMemo(
    () =>
      Yup.object<SalaryDeviationFormTypes.CreateLeave>({
        personId: Yup.number().required(TranslationKeys.Validations.Required),
        personName: Yup.string().required(TranslationKeys.Validations.Required),
        customName: Yup.string().when(
          FormFieldConstants.SalaryDeviation.CreateLeave.DeviationType,
          ([type], schema: Yup.StringSchema) =>
            (type === WintSuperkollAPIModel.SalaryDeviationType.CustomTimeDeviation &&
              schema.required(TranslationKeys.Validations.Required)) ||
            schema
        ),
        yearAndMonth: Yup.number(),
        deviationType: Yup.mixed()
          .oneOf(Object.values(WintSuperkollAPIModel.SalaryDeviationType))
          .required(TranslationKeys.Validations.Required),
        fromDate: Yup.date()
          .typeError(TranslationKeys.Validations.InvalidDate)
          .required(TranslationKeys.Validations.Required)
          .when(
            FormFieldConstants.SalaryDeviation.CreateLeave.RestrictDateRange,
            ([dateRange], schema: Yup.DateSchema) =>
              (dateRange?.start &&
                dateRange?.end &&
                schema
                  .min(dateRange.start, TranslationKeys.SalaryDeviation.FromAndTwoDatesShouldBeInExistingDeviationMonth)
                  .max(
                    dateRange.end,
                    TranslationKeys.SalaryDeviation.FromAndTwoDatesShouldBeInExistingDeviationMonth
                  )) ||
              schema
          ),
        toDate: Yup.date()
          .typeError(TranslationKeys.Validations.InvalidDate)
          .required(TranslationKeys.Validations.Required)
          .when(
            FormFieldConstants.SalaryDeviation.CreateLeave.FromDate,
            ([fromDate], schema: Yup.DateSchema) =>
              (fromDate &&
                schema.min(
                  Yup.ref(FormFieldConstants.SalaryDeviation.CreateLeave.FromDate),
                  TranslationKeys.SalaryDeviation.FromAndTwoDatesShouldBeInExistingDeviationMonth
                )) ||
              schema
          )
          .when(
            FormFieldConstants.SalaryDeviation.CreateLeave.RestrictDateRange,
            ([dateRange], schema: Yup.DateSchema) =>
              (dateRange?.start &&
                dateRange?.end &&
                schema
                  .min(dateRange.start, TranslationKeys.SalaryDeviation.FromAndTwoDatesShouldBeInExistingDeviationMonth)
                  .max(
                    dateRange.end,
                    TranslationKeys.SalaryDeviation.FromAndTwoDatesShouldBeInExistingDeviationMonth
                  )) ||
              schema
          ),
        dayHours: Yup.array<SalaryDeviationFormTypes.HoursByDay>().when(
          FormFieldConstants.SalaryDeviation.CreateLeave.DeviationType,
          (
            [type]: [WintSuperkollAPIModel.SalaryDeviationType],
            schema: ArraySchema<SalaryDeviationFormTypes.HoursByDay>
          ) => {
            const isCustomDeviation = type === WintSuperkollAPIModel.SalaryDeviationType.CustomTimeDeviation;
            const isTimeReportingDeviation = SalaryDeviationConstants.TimeReportingDeviationTypes.some(
              (t) => type === t
            );
            return schema.of(
              Yup.object<SalaryDeviationFormTypes.HoursByDay>().shape(
                {
                  id: Yup.number(),
                  day: Yup.date().typeError(TranslationKeys.Validations.InvalidDate).required(),
                  hours: Yup.number()
                    .when(
                      FormFieldConstants.SalaryDeviation.HoursByDay.Minutes,
                      ([minutes], schema: Yup.NumberSchema) => (!minutes && schema.required().min(0)) || schema
                    )
                    // eslint-disable-next-line no-empty-pattern
                    .when([], ([], schema: Yup.NumberSchema) => (isCustomDeviation && schema) || schema.max(8))
                    // eslint-disable-next-line no-empty-pattern
                    .when([], ([], schema: Yup.NumberSchema) => (isTimeReportingDeviation && schema.max(24)) || schema),
                  minutes: Yup.number().when(
                    FormFieldConstants.SalaryDeviation.HoursByDay.Hours,
                    ([hours], schema: Yup.NumberSchema) =>
                      (((isTimeReportingDeviation && hours > 23) ||
                        (!isCustomDeviation && !isTimeReportingDeviation && hours > 7)) &&
                        schema.max(0)) ||
                      schema.max(59)
                  ),
                },
                [
                  [
                    FormFieldConstants.SalaryDeviation.HoursByDay.Minutes,
                    FormFieldConstants.SalaryDeviation.HoursByDay.Hours,
                  ],
                ]
              )
            );
          }
        ),
        fillDayHours: Yup.boolean(),
        childName: Yup.string().when(
          FormFieldConstants.SalaryDeviation.CreateLeave.DeviationType,
          ([type], schema: Yup.StringSchema) =>
            ([
              WintSuperkollAPIModel.SalaryDeviationType.ParentalLeave,
              WintSuperkollAPIModel.SalaryDeviationType.TenDays,
            ].some((t) => t === type) &&
              schema.required(TranslationKeys.Validations.Required)) ||
            schema
        ),
        comment: Yup.string(),
        restrictDateRange: Yup.object(),
        hasConnectedTimeReports: Yup.boolean(),
        isAdminOnlyType: Yup.boolean(),
      }),
    []
  );

  const handleFormSubmit = async (values: SalaryDeviationFormTypes.CreateLeave) => {
    const deviation: WintSuperkollAPIModel.SalaryDeviationPost = {
      personId: values.personId,
      customDeviationName: values.customName,
      yearAndMonth: values.yearAndMonth,
      deviationType: values.deviationType,
      childName: values.childName,
      fromDate: DateHelper.resetTime(values.fromDate),
      toDate: DateHelper.resetTime(values.toDate),
      comment: values.comment,
      dayHours:
        ![
          WintSuperkollAPIModel.SalaryDeviationType.Vacation,
          WintSuperkollAPIModel.SalaryDeviationType.TenDays,
          WintSuperkollAPIModel.SalaryDeviationType.PaidShortTermLeave,
        ].some((t) => t === values.deviationType) && values.fillDayHours
          ? values.dayHours?.map(
              (item) =>
                ({
                  day: item.day,
                  hours: +item.hours + +item.minutes / 60,
                }) as WintSuperkollAPIModel.SalaryDeviationHoursByDay
            )
          : [],
    };

    const result = salaryDeviationId
      ? await salaryDeviationsService.update({ ...deviation, id: salaryDeviationId })
      : await salaryDeviationsService.create(deviation);

    if (result.data.errorMessage && result.data.errorMessage?.length > 0) {
      enqueueMessage(result.data.errorMessage, StyleEnums.DisplayType.Danger);
      throw new Error(result.data.errorMessage);
    }
  };

  const leaveDatesWorkerInstance = React.useMemo(() => WorkerHelper.getSalaryDeviationLeaveDatesWorker(), []);

  const onDatesChange = (name: string, value: any, formikProps: FormikProps<SalaryDeviationFormTypes.CreateLeave>) => {
    const { setFieldValue, setFieldTouched, values } = formikProps;
    const startDate = values.fromDate;
    const endDate = values.toDate;

    if (!startDate || !endDate || !values.fillDayHours) return;

    leaveDatesWorkerInstance.onmessage = (e: MessageEvent<SalaryDeviationFormTypes.HoursByDay[]>) => {
      setFieldValue(FormFieldConstants.SalaryDeviation.CreateLeave.DayHours, e.data);
      setFieldTouched(FormFieldConstants.SalaryDeviation.CreateLeave.DayHours);
    };

    leaveDatesWorkerInstance.postMessage({ startDate, endDate, existingDayHours: values.dayHours });
  };

  const { getIsReadOnly } = useIsReadOnly();
  const isReadOnly = getIsReadOnly({
    hasConnectedTimeReports: initialValues.hasConnectedTimeReports,
    isAdminOnlyType: initialValues.isAdminOnlyType ?? false,
    isMonthDisabled: salaryDeviationsService.isMonthDisabled(monthState),
  });

  return (
    <>
      {loading && (
        <div className={cssExports.dialogLoadingContainer}>
          <LoadingWithLogo />
        </div>
      )}
      {initialValues.hasConnectedTimeReports && (
        <StaticInfoMessage gutterBottom>
          {LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SalaryDeviation.ConnectedToProjectTimeInfoMessage
          )}
        </StaticInfoMessage>
      )}
      <CreateLeaveForm
        ref={ref}
        defaultValues={initialValues}
        validationSchema={validationSchema}
        handleSubmit={handleFormSubmit}
        onFormValidationChange={props.onFormValidationChange}
        isSalaryAdmin={isSalaryAdmin}
        isBusinessUnitManagerWithSalaryRights={isBusinessUnitManagerWithSalaryRights}
        isDeviationAdmin={isDeviationAdmin}
        datesChangeHandler={onDatesChange}
        allowedDeviationTypes={enabledLeaveTypes}
        yearMonth={yearMonth}
        disabled={isReadOnly}
        isNew={salaryDeviationId ? false : true}
      />
    </>
  );
};

export default forwardRef(CreateLeaveFormContainer);
